import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  FilterOutlined,
  ProjectOutlined,
  ShoppingOutlined,
  UserOutlined,
} from "@ant-design/icons";
import {
  Badge,
  Button,
  Card,
  Col,
  Empty,
  Input,
  Pagination,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Tag,
  Typography,
} from "antd";
import { useState } from "react";
import { Link } from "react-router-dom";
import { PurchaseRequestStatus, PurchaseTaskType } from "../../enum/common";
import { IPurchaseFilter, IPurchaseRequest } from "../../models/Purchase";
import { useGetPurchaseOrderAssignment } from "../../query/PurchaseQuery";

const { Text } = Typography;
const { Search } = Input;

const PurchaseRequestAssign = () => {
  const [filter, setFilter] = useState<IPurchaseFilter>({
    page: 1,
    limit: 10,
  });
  const { data, isLoading } = useGetPurchaseOrderAssignment(filter);

  const getStatusTag = (status: PurchaseRequestStatus) => {
    switch (status) {
      case PurchaseRequestStatus.DRAFT:
        return (
          <Tag icon={<ClockCircleOutlined />} color="orange">
            Chờ xử lý
          </Tag>
        );
      case PurchaseRequestStatus.SUBMITTED:
        return (
          <Tag icon={<ClockCircleOutlined />} color="default">
            Chờ duyệt
          </Tag>
        );
      case PurchaseRequestStatus.APPROVED:
        return (
          <Tag icon={<CheckCircleOutlined />} color="blue">
            Đang thực hiện
          </Tag>
        );
      case PurchaseRequestStatus.CLOSED:
        return (
          <Tag icon={<CheckCircleOutlined />} color="green">
            Dã đóng/Hoàn thành
          </Tag>
        );
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const getSourceLabel = (source: string) => {
    if (source === PurchaseTaskType.RD_REQUEST) return "R&D";
    if (source === PurchaseTaskType.SALE_ORDER) return "Sale";
    if (source === PurchaseTaskType.DIRECTOR) return "Giám đốc";
    return source;
  };

  const stats = {
    total: data?.pagination.total || 0,
    pending:
      data?.data.filter((o) => o.status === PurchaseRequestStatus.DRAFT)
        .length || 0,
    inProgress:
      data?.data.filter((o) => o.status === PurchaseRequestStatus.APPROVED)
        .length || 0,
    completed:
      data?.data.filter((o) => o.status === PurchaseRequestStatus.CLOSED)
        .length || 0,
  };

  return (
    <div className="p-4 min-h-96">
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} lg={6}>
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
            <Statistic
              title={<span className="text-white">Tổng đơn hàng</span>}
              value={stats.total}
              valueStyle={{ color: "white" }}
              prefix={<ShoppingOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0">
            <Statistic
              title={<span className="text-white">Chờ xử lý</span>}
              value={stats.pending}
              valueStyle={{ color: "white" }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
            <Statistic
              title={<span className="text-white">Đang thực hiện</span>}
              value={stats.inProgress}
              valueStyle={{ color: "white" }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
            <Statistic
              title={<span className="text-white">Hoàn thành</span>}
              value={stats.completed}
              valueStyle={{ color: "white" }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card className="mb-6">
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12} lg={8}>
            <Search
              placeholder="Tìm theo mã đơn hàng..."
              allowClear
              onSearch={(value) =>
                setFilter({ ...filter, page: 1, pONumber: value })
              }
            />
          </Col>
          <Col xs={24} md={12} lg={8}>
            <Select
              className="w-full"
              placeholder="Lọc theo trạng thái"
              allowClear
              onChange={(value) =>
                setFilter({ ...filter, page: 1, status: value })
              }
              options={[
                { label: "Chờ xử lý", value: PurchaseRequestStatus.DRAFT },
                {
                  label: "Đang thực hiện",
                  value: PurchaseRequestStatus.APPROVED,
                },
                {
                  label: "Hoàn thành/Đã đóng",
                  value: PurchaseRequestStatus.CLOSED,
                },
              ]}
              suffixIcon={<FilterOutlined />}
            />
          </Col>
          <Col xs={24} md={12} lg={8}>
            <Select
              className="w-full"
              placeholder="Lọc theo nguồn"
              allowClear
              onChange={(value) =>
                setFilter({ ...filter, page: 1, source: value })
              }
              options={[
                { label: "R&D", value: PurchaseTaskType.RD_REQUEST },
                { label: "Sale", value: PurchaseTaskType.SALE_ORDER },
                { label: "Giám đốc", value: PurchaseTaskType.DIRECTOR },
              ]}
            />
          </Col>
        </Row>
      </Card>

      {isLoading ? (
        <div className="flex justify-center items-center py-20">
          <Spin size="large" />
        </div>
      ) : !data?.data || data.data.length === 0 ? (
        <Card>
          <Empty
            description="Chưa có đơn hàng nào được giao cho bạn"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      ) : (
        <>
          <Row gutter={[16, 16]}>
            {data.data.map((order: IPurchaseRequest) => (
              <Col xs={24} sm={12} lg={8} key={order.id}>
                <Badge.Ribbon
                  text={getSourceLabel(order.source)}
                  color={
                    order.source === PurchaseTaskType.RD_REQUEST
                      ? "purple"
                      : order.source === PurchaseTaskType.SALE_ORDER
                      ? "cyan"
                      : "gold"
                  }
                >
                  <Card
                    hoverable
                    className="h-full"
                    actions={[
                      <Link
                        to={`/purchase/requests/${order.requestNumber}`}
                        key="view"
                      >
                        <Button type="link" icon={<EyeOutlined />}>
                          Xem chi tiết
                        </Button>
                      </Link>,
                    ]}
                  >
                    <div className="mb-3">
                      <Text strong className="text-lg block mb-2">
                        Mã yêu cầu: {order.requestNumber}
                      </Text>
                      Trạng thái: {getStatusTag(order.status)}
                    </div>

                    <Space direction="vertical" className="w-full" size="small">
                      <div className="flex items-center gap-2">
                        <ProjectOutlined className="text-purple-500" />
                        <Text type="secondary" className="text-sm">
                          Dự án: {order.projectName || "N/A"}
                        </Text>
                      </div>

                      <div className="flex items-center gap-2">
                        <UserOutlined className="text-blue-500" />
                        <Text type="secondary" className="text-sm">
                          Người yêu cầu: {order.requestBy}
                        </Text>
                      </div>

                      <div className="flex items-center gap-2">
                        <ClockCircleOutlined className="text-gray-400" />
                        <Text type="secondary" className="text-xs">
                          Ngày yêu cầu:{" "}
                          {new Date(order.createdAt).toLocaleDateString(
                            "vi-VN"
                          )}
                        </Text>
                      </div>
                    </Space>

                    {order.note && (
                      <div className="mt-3 p-2 bg-gray-50 dark:bg-gray-800 rounded">
                        <Text type="secondary" className="text-xs italic">
                          Ghi chú:{" "}
                          {order.note.length > 60
                            ? `${order.note.substring(0, 60)}...`
                            : order.note}
                        </Text>
                      </div>
                    )}
                  </Card>
                </Badge.Ribbon>
              </Col>
            ))}
          </Row>

          <div className="flex justify-center mt-6">
            <Pagination
              current={data.pagination.page}
              pageSize={data.pagination.limit}
              total={data.pagination.total}
              onChange={(page, pageSize) =>
                setFilter({ ...filter, page, limit: pageSize })
              }
              showSizeChanger
              showTotal={(total) => `Tổng ${total} đơn hàng`}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default PurchaseRequestAssign;
